import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Download, Users, FileText, UserCog, Music2, Sparkles, Copy, Check, Loader2, Ticket, UserPlus, TrendingUp, Receipt } from 'lucide-react';
import EventFormDialog from '../components/admin/EventFormDialog';
import AssignOrganizerDialog from '../components/admin/AssignOrganizerDialog';
import PromotionsPanel from '../components/admin/PromotionsPanel';
import WaitlistPanel from '../components/admin/WaitlistPanel';
import UpdatesPanel from '../components/admin/UpdatesPanel';
import UsersPanel from '../components/admin/UsersPanel';
import ArtistsPanel from '../components/admin/ArtistsPanel';
import TicketsPanel from '../components/admin/TicketsPanel';
import PromoterAnalyticsPanel from '../components/common/PromoterAnalyticsPanel';
import OfflineTicketDialog from '../components/organizer/OfflineTicketDialog';
import { InternalListPanel } from '../components/organizer/InternalListPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  title: '',
  date: '',
  time: '',
  venue: '',
  address: '',
  genre: '',
  description: '',
  image_url: '',
  lineup: '',
  is_featured: false,
  waitlist_only: false,
  min_age: 21,
  terms_conditions: '',
  event_type: 'ticketed',
  organizer_email: '',
  sales_closed: false,
  guest_list_closed: false,
  sales_close_time: null,
  guestlist_close_time: null,
  platform_fee_per_ticket: null,
  convenience_fee_per_ticket: null,
  waitlist_categories: ['stag_male'],
  is_multi_day: false,
  event_dates: [],
  ticket_tiers: [{ name: '', price: '', quantity_available: '', description: '', hide_remaining: false, valid_date_ids: null }],
};

const AdminDashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('events');
  // Offline ticketing dialog state (admin can issue door tickets per event)
  const [offlineEvent, setOfflineEvent] = useState(null);

  // Events state
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  // Organizer-assignment dialog state
  const [assigningEvent, setAssigningEvent] = useState(null);

  // Waitlist state
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSearch, setWaitlistSearch] = useState('');
  const [waitlistFilter, setWaitlistFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM);

  // Demo-seed state (one-click "spin up demo guest list" button)
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [copied, setCopied] = useState('');

  // Launch reset — wipes every event + child collection and seeds BohoB • AKYR.
  // Used once in production after code deploy to purge dummy legacy events.
  const [resetting, setResetting] = useState(false);

  // Email diagnostic — send a test email and view Resend's raw response.
  // Handy for verifying prod DNS + RESEND_API_KEY + SENDER_EMAIL wiring.
  const [emailTestTo, setEmailTestTo] = useState('');
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const handleEmailTest = async () => {
    if (emailTesting) return;
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/email-test`,
        emailTestTo ? { to: emailTestTo } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailTestResult(res.data);
      if (res.data.ok) toast.success(`Sent · Resend id ${res.data.id?.slice(0, 8)}…`);
      else toast.error(res.data.error || 'Send failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnostic call failed');
      setEmailTestResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setEmailTesting(false);
    }
  };
  const handleLaunchReset = async () => {
    if (resetting) return;
    const ok = window.confirm(
      'This will DELETE every event, guest list, guest, ticket and waitlist entry in this database, then seed the BohoB • AKYR launch event. Are you sure?'
    );
    if (!ok) return;
    const again = window.prompt('Type RESET to confirm (case-sensitive):');
    if (again !== 'RESET') {
      toast.info('Cancelled');
      return;
    }
    setResetting(true);
    try {
      const res = await axios.post(
        `${API}/admin/reset-and-seed-launch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Wiped ${res.data.wiped.events} events + children · seeded ${res.data.seeded_event.title}`);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleSeedDemo = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const res = await axios.post(
        `${API}/admin/seed-demo-guestlist`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSeedResult(res.data);
      toast.success('Demo guest list ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not seed demo');
    } finally {
      setSeeding(false);
    }
  };

  const copyValue = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      if (!user.is_admin) {
        toast.error('Admin access required');
        navigate('/');
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchEventsInit = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token && user?.is_admin) fetchEventsInit();
  }, [token, user]);

  // Shared refresh so dialogs can request fresh event list after mutations
  const refreshEvents = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
      // Keep the open OfflineTicketDialog's `event` prop in sync so the
      // "N left" tier count refreshes after each issued ticket.
      setOfflineEvent((cur) => cur ? (response.data.find((e) => e.id === cur.id) || cur) : cur);
    } catch (error) {
      console.error('Error refreshing events:', error);
    }
  };

  // Fetch waitlist when tab changes to waitlist
  useEffect(() => {
    const fetchWaitlist = async () => {
      if (!token || activeTab !== 'waitlist') return;
      setWaitlistLoading(true);
      try {
        const response = await axios.get(`${API}/waitlist/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWaitlist(response.data);
      } catch (error) {
        console.error('Error fetching waitlist:', error);
        toast.error('Failed to load waitlist');
      } finally {
        setWaitlistLoading(false);
      }
    };
    if (token && user?.is_admin && activeTab === 'waitlist') fetchWaitlist();
  }, [token, user, activeTab]);

  const handleExportWaitlist = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `unight_waitlist_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Waitlist exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export waitlist');
    }
  };

  const filteredWaitlist = waitlist.filter((entry) => {
    const matchesSearch =
      entry.name?.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
      entry.email?.toLowerCase().includes(waitlistSearch.toLowerCase());
    const matchesFilter = waitlistFilter === 'all' || entry.city === waitlistFilter;
    return matchesSearch && matchesFilter;
  });

  const uniqueCities = [...new Set(waitlist.map((e) => e.city))];

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setEditingEvent(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowEventModal(true);
  };

  const handleOpenEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      date: event.date,
      time: event.time,
      venue: event.venue,
      address: event.address,
      genre: event.genre,
      description: event.description,
      image_url: event.image_url,
      lineup: event.lineup.join(', '),
      is_featured: event.is_featured,
      waitlist_only: !!event.waitlist_only,
      min_age: event.min_age ?? 21,
      terms_conditions: event.terms_conditions || '',
      event_type: event.event_type || 'ticketed',
      organizer_email: event.organizer_email || '',
      sales_closed: !!event.sales_closed,
      guest_list_closed: !!event.guest_list_closed,
      sales_close_time: event.sales_close_time ?? null,
      guestlist_close_time: event.guestlist_close_time ?? null,
      platform_fee_per_ticket: event.platform_fee_per_ticket ?? null,
      convenience_fee_per_ticket: event.convenience_fee_per_ticket ?? null,
      waitlist_categories: Array.isArray(event.waitlist_categories)
        ? event.waitlist_categories
        : ['stag_male'],
      is_multi_day: !!event.is_multi_day,
      event_dates: Array.isArray(event.event_dates) ? event.event_dates : [],
      ticket_tiers: event.ticket_tiers.map((t) => ({
        id: t.id,
        name: t.name,
        price: t.price.toString(),
        quantity_available: t.quantity_available.toString(),
        description: t.description || '',
        hide_remaining: !!t.hide_remaining,
        valid_date_ids: Array.isArray(t.valid_date_ids) ? t.valid_date_ids : null,
      })),
    });
    setShowEventModal(true);
  };

  const handleAddTier = () => {
    setFormData({
      ...formData,
      ticket_tiers: [
        ...formData.ticket_tiers,
        { name: '', price: '', quantity_available: '', description: '', hide_remaining: false },
      ],
    });
  };

  const handleRemoveTier = (index) => {
    setFormData({
      ...formData,
      ticket_tiers: formData.ticket_tiers.filter((_, i) => i !== index),
    });
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...formData.ticket_tiers];
    newTiers[index][field] = value;
    setFormData({ ...formData, ticket_tiers: newTiers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        min_age: parseInt(formData.min_age) || 21,
        terms_conditions: (formData.terms_conditions || '').trim() || null,
        event_type: formData.event_type || 'ticketed',
        organizer_email: (formData.organizer_email || '').trim() || null,
        sales_closed: !!formData.sales_closed,
        guest_list_closed: !!formData.guest_list_closed,
        sales_close_time: formData.sales_close_time || null,
        guestlist_close_time: formData.guestlist_close_time || null,
        platform_fee_per_ticket: formData.platform_fee_per_ticket ?? null,
        convenience_fee_per_ticket: formData.convenience_fee_per_ticket ?? null,
        waitlist_categories: Array.isArray(formData.waitlist_categories)
          ? formData.waitlist_categories
          : ['stag_male'],
        is_multi_day: !!formData.is_multi_day,
        event_dates: formData.is_multi_day && Array.isArray(formData.event_dates)
          ? formData.event_dates.filter((d) => d.date && d.time).map((d) => ({
              id: d.id,
              date: d.date,
              time: d.time,
              label: d.label || null,
            }))
          : [],
        lineup: formData.lineup.split(',').map((a) => a.trim()).filter(Boolean),
        ticket_tiers: formData.event_type === 'guest_list' ? [] : formData.ticket_tiers.map((t) => ({
          ...(t.id && { id: t.id }),
          name: t.name,
          price: parseFloat(t.price),
          quantity_available: parseInt(t.quantity_available),
          description: t.description || null,
          hide_remaining: !!t.hide_remaining,
          valid_date_ids: Array.isArray(t.valid_date_ids) && t.valid_date_ids.length > 0 ? t.valid_date_ids : null,
        })),
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Event created');
      }

      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
      setShowEventModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(events.filter((e) => e.id !== eventId));
      toast.success('Event deleted');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="loading">
        <div className="text-[#B5FF00] font-mono uppercase tracking-widest animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="admin-dashboard">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">
              ADMIN PANEL
            </span>
            <h1 className="font-['Josefin_Sans'] text-5xl md:text-6xl lg:text-7xl font-bold text-white">
              {activeTab === 'events' && 'MANAGE EVENTS'}
              {activeTab === 'tickets' && 'TICKETS SOLD'}
              {activeTab === 'waitlist' && 'WAITLIST'}
              {activeTab === 'updates' && 'UPDATES'}
              {activeTab === 'users' && 'USERS'}
              {activeTab === 'artists' && 'DISCOVERY ARTISTS'}
              {activeTab === 'promotions' && 'PROMOTIONS'}
              {activeTab === 'analytics' && 'PROMOTER ANALYTICS'}
            </h1>
          </div>

          {activeTab === 'events' && (
            <button
              onClick={handleOpenCreate}
              className="btn-primary flex items-center gap-2"
              data-testid="create-event-btn"
            >
              <Plus size={18} />
              CREATE EVENT
            </button>
          )}

          {activeTab === 'waitlist' && (
            <button
              onClick={handleExportWaitlist}
              className="btn-primary flex items-center gap-2"
              data-testid="export-waitlist-btn"
            >
              <Download size={18} />
              EXPORT CSV
            </button>
          )}
        </div>

        {/* Demo seed banner — one-click spin-up of a complete client demo */}
        <div className="mb-8 border border-[#B5FF00]/30 bg-[#B5FF00]/5 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="demo-seed-banner">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={16} className="text-[#B5FF00]" />
              <span className="font-mono text-xs text-[#B5FF00] tracking-widest">DEMO GUEST LIST</span>
            </div>
            <p className="text-zinc-300 text-sm">
              One-click spin-up of a sample event + promoter + guest list (with 15 sample guests and a shareable RSVP code) for client demos. Safe to re-run — it never duplicates.
            </p>
          </div>
          <button
            onClick={handleSeedDemo}
            disabled={seeding}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
            data-testid="seed-demo-btn"
          >
            {seeding ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {seeding ? 'SPINNING UP…' : 'SET UP DEMO'}
          </button>
        </div>

        {seedResult && (
          <div className="mb-8 border border-zinc-800 bg-[#0A0A0A] p-6" data-testid="demo-seed-result">
            <div className="font-mono text-xs text-[#B5FF00] tracking-widest mb-3">DEMO READY — SHARE THESE WITH THE CLIENT</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <SeedRow label="PROMOTER EMAIL" value={seedResult.promoter_email} copied={copied} onCopy={copyValue} />
              <SeedRow label="PROMOTER PASSWORD" value={seedResult.promoter_password} copied={copied} onCopy={copyValue} />
              <SeedRow label="RSVP CODE" value={seedResult.rsvp_code} copied={copied} onCopy={copyValue} />
              <SeedRow label="RSVP URL" value={seedResult.rsvp_url} copied={copied} onCopy={copyValue} />
            </div>
            <p className="font-mono text-xs text-zinc-500 mt-4 tracking-wider">
              Sign in as the promoter → Guest List tab → Demo VIP → walk the client through it live.
            </p>
          </div>
        )}


        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-zinc-800 overflow-x-auto">
          <button
            onClick={() => setActiveTab('events')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors ${
              activeTab === 'events'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-events"
          >
            EVENTS ({events.length})
          </button>
          <button
            onClick={() => setActiveTab('waitlist')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'waitlist'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-waitlist"
          >
            <Users size={16} />
            WAITLIST ({waitlist.length})
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'tickets'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-tickets"
          >
            <Ticket size={16} />
            TICKETS
          </button>
          <button
            onClick={() => setActiveTab('updates')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'updates'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-updates"
          >
            <FileText size={16} />
            UPDATES
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'users'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-users"
          >
            <UserCog size={16} />
            USERS
          </button>
          <button
            onClick={() => setActiveTab('artists')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'artists'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-artists"
          >
            <Music2 size={16} />
            ARTISTS
          </button>
          <button
            onClick={() => setActiveTab('promotions')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'promotions'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-promotions"
          >
            <Sparkles size={16} />
            PROMOTIONS
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-4 px-2 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'analytics'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-analytics"
          >
            <TrendingUp size={16} />
            PROMOTERS
          </button>
        </div>

        {/* Events Tab Content */}
        {activeTab === 'events' && (
          <div className="overflow-x-auto">
            {/* Email diagnostic — verify Resend + DNS + SENDER_EMAIL wiring per env */}
            <div
              className="mb-6 border border-[#B5FF00]/40 bg-[#B5FF00]/5 p-4 flex flex-col gap-3"
              data-testid="email-diagnostic-panel"
            >
              <div>
                <p className="font-mono text-xs text-[#B5FF00] tracking-widest">EMAIL DIAGNOSTIC · RESEND WIRING</p>
                <p className="text-zinc-400 text-sm mt-1">
                  Sends a branded test email using this environment&apos;s <span className="font-mono text-[#B5FF00]">RESEND_API_KEY</span> and <span className="font-mono text-[#B5FF00]">SENDER_EMAIL</span>. Response below shows the raw Resend id or error — use it on Production to prove emails are reaching Resend.
                  Leave the recipient blank to send to <span className="font-mono text-[#B5FF00]">ADMIN_NOTIFY_EMAIL</span>.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={emailTestTo}
                  onChange={(e) => setEmailTestTo(e.target.value)}
                  placeholder="recipient@example.com (blank = ADMIN_NOTIFY_EMAIL)"
                  className="flex-1 bg-black border border-zinc-800 focus:border-[#B5FF00] outline-none px-3 py-2 font-mono text-xs text-white"
                  data-testid="email-test-to-input"
                />
                <button
                  onClick={handleEmailTest}
                  disabled={emailTesting}
                  className="border border-[#B5FF00] text-[#B5FF00] hover:bg-[#B5FF00] hover:text-black transition-colors px-4 py-2 font-mono text-xs tracking-widest disabled:opacity-50 shrink-0"
                  data-testid="email-test-btn"
                >
                  {emailTesting ? 'SENDING…' : 'SEND TEST EMAIL'}
                </button>
              </div>
              {emailTestResult && (
                <pre
                  className="mt-2 bg-black border border-zinc-800 p-3 font-mono text-[11px] text-zinc-300 overflow-x-auto max-h-64"
                  data-testid="email-test-result"
                >
                  {JSON.stringify(emailTestResult, null, 2)}
                </pre>
              )}
            </div>

            {/* Danger Zone — one-shot launch reset (wipe all + seed BohoB) */}
            <div
              className="mb-6 border border-red-500/40 bg-red-500/5 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              data-testid="launch-reset-panel"
            >
              <div>
                <p className="font-mono text-xs text-red-400 tracking-widest">DANGER · ONE-SHOT LAUNCH RESET</p>
                <p className="text-zinc-400 text-sm mt-1">
                  Wipes every event + guest list + ticket + waitlist entry in this database, then seeds the BohoB • AKYR launch event.
                  Use once on production to clear legacy dummy events, then edit the seeded event to add your real artwork.
                </p>
              </div>
              <button
                onClick={handleLaunchReset}
                disabled={resetting}
                className="border border-red-500 text-red-400 hover:bg-red-500 hover:text-white transition-colors px-4 py-2 font-mono text-xs tracking-widest disabled:opacity-50 shrink-0"
                data-testid="launch-reset-btn"
              >
                {resetting ? 'RESETTING…' : 'RESET & SEED BOHOB'}
              </button>
            </div>
            <table className="w-full" data-testid="events-table">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">EVENT</th>
                  <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">DATE</th>
                  <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">VENUE</th>
                  <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">GENRE</th>
                  <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">FEATURED</th>
                  <th className="text-right py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <Fragment key={event.id}>
                  <tr
                    className="border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors"
                    data-testid={`admin-event-${event.id}`}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-4">
                        <img src={event.image_url} alt={event.title} className="w-12 h-12 object-cover" />
                        <span className="font-['Josefin_Sans'] text-lg text-white">{event.title}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 font-mono text-sm text-zinc-400">{event.date}</td>
                    <td className="py-4 px-4 text-sm text-zinc-400">{event.venue}</td>
                    <td className="py-4 px-4">
                      <span className="font-mono text-xs text-[#B5FF00] bg-[#B5FF00]/10 px-2 py-1">
                        {event.genre}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      {event.is_featured && (
                        <span className="font-mono text-xs text-white bg-zinc-800 px-2 py-1">YES</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {event.event_type !== 'guest_list' && (
                          <button
                            onClick={() => setOfflineEvent(event)}
                            className="p-2 text-zinc-400 hover:text-[#B5FF00] transition-colors"
                            data-testid={`offline-ticket-${event.id}`}
                            title="Issue offline ticket"
                          >
                            <Receipt size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => setAssigningEvent(event)}
                          className="p-2 text-zinc-400 hover:text-[#B5FF00] transition-colors"
                          data-testid={`assign-organizer-${event.id}`}
                          title="Assign organizer"
                        >
                          <UserPlus size={18} />
                        </button>
                        <button
                          onClick={() => handleOpenEdit(event)}
                          className="p-2 text-zinc-400 hover:text-white transition-colors"
                          data-testid={`edit-event-${event.id}`}
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(event.id)}
                          className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {/* Internal guest list sub-row — admins + internal users only */}
                  <tr className="border-b border-zinc-800/50">
                    <td colSpan={6} className="px-4 pb-4">
                      <InternalListPanel eventId={event.id} eventTitle={event.title} token={token} user={user} />
                    </td>
                  </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>

            {events.length === 0 && (
              <div className="text-center py-16 border border-zinc-800">
                <p className="text-zinc-500 font-mono">No events yet. Create your first event.</p>
              </div>
            )}
          </div>
        )}

        {/* Waitlist Tab Content */}
        {activeTab === 'waitlist' && (
          <WaitlistPanel
            waitlist={waitlist}
            waitlistLoading={waitlistLoading}
            waitlistSearch={waitlistSearch}
            setWaitlistSearch={setWaitlistSearch}
            waitlistFilter={waitlistFilter}
            setWaitlistFilter={setWaitlistFilter}
            filteredWaitlist={filteredWaitlist}
            uniqueCities={uniqueCities}
          />
        )}

        {/* Updates Tab Content */}
        {activeTab === 'tickets' && <TicketsPanel events={events} token={token} />}

        {activeTab === 'updates' && <UpdatesPanel token={token} />}

        {/* Users Tab Content */}
        {activeTab === 'users' && <UsersPanel token={token} />}

        {/* Artists Tab Content */}
        {activeTab === 'artists' && <ArtistsPanel token={token} />}
        {activeTab === 'promotions' && <PromotionsPanel token={token} />}
        {activeTab === 'analytics' && <PromoterAnalyticsPanel token={token} events={events} />}
      </div>

      {/* Event Modal */}
      <EventFormDialog
        open={showEventModal}
        onOpenChange={(open) => {
          setShowEventModal(open);
          if (!open) resetForm();
        }}
        editingEvent={editingEvent}
        formData={formData}
        setFormData={setFormData}
        saving={saving}
        onSubmit={handleSubmit}
        onCancel={() => { setShowEventModal(false); resetForm(); }}
        onAddTier={handleAddTier}
        onRemoveTier={handleRemoveTier}
        onTierChange={handleTierChange}
      />
      <AssignOrganizerDialog
        open={!!assigningEvent}
        onOpenChange={(v) => !v && setAssigningEvent(null)}
        event={assigningEvent}
        token={token}
        onChanged={() => { /* No full refresh needed — assignment doesn't change the events list */ }}
      />
      <OfflineTicketDialog
        open={!!offlineEvent}
        onOpenChange={(v) => !v && setOfflineEvent(null)}
        event={offlineEvent}
        token={token}
        onIssued={() => refreshEvents()}
      />
    </div>
  );
};

export default AdminDashboard;

const SeedRow = ({ label, value, copied, onCopy }) => (
  <div className="flex items-center justify-between gap-3 bg-[#050505] border border-zinc-800 px-3 py-2">
    <div className="min-w-0 flex-1">
      <div className="font-mono text-[10px] text-zinc-500 tracking-widest">{label}</div>
      <div className="font-mono text-sm text-white truncate" title={value}>{value}</div>
    </div>
    <button
      onClick={() => onCopy(label, value)}
      className="p-2 text-zinc-400 hover:text-[#B5FF00]"
      data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
      title="Copy"
    >
      {copied === label ? <Check size={14} className="text-[#B5FF00]" /> : <Copy size={14} />}
    </button>
  </div>
);
